import { findSceneDataBlock } from './findSceneDataBlock';

/**
 * OPUS-1619 — reading a graphic's NLE caption block out of the scene.
 *
 * Separated from the hook so it can be exercised against a real scene without
 * a browser. That matters: every wrong turn in this feature so far has been a
 * guess about a URL or a caption name that a single run against the live CDN
 * would have settled.
 */

export const stripFileName = (path) =>
  typeof path === 'string' && path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : null;

export async function getJson(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    // The scenes host serves its SPA shell for a missing file, as 200
    // text/html — so an unguarded res.json() throws rather than 404ing, and a
    // status check alone reports every missing caption as present.
    if (!(res.headers.get('content-type') || '').includes('json')) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Which captions a graphic has. The definition indexes them under
 * v_captions.refMap, so the names are discovered rather than guessed —
 * gfx_PermScore alone offers 82 of them.
 */
export function captionNames(definition) {
  const refMap = definition?.v_captions?.refMap;
  return refMap && typeof refMap === 'object' ? Object.keys(refMap) : [];
}

/**
 * The caption to try first when nothing else names one. Scene-wide default
 * that the operator's own choice overrides the moment they use a caption.
 */
const DEFAULT_CAPTION = 'DefaultPopulated';

/**
 * How many captions to try per graphic before giving up.
 *
 * BOUNDED, because gfx_PermScore alone indexes 82 of them and walking every
 * one is what turned page load into a request storm. Now that fetching is
 * on-demand and cached per layer this cost is paid once, on the first event
 * that plays a graphic, so it can afford to look past the first name or two.
 *
 * Eight is chosen against the real scene: the post graphics the builders play
 * index ONE caption each (GoalScorer, Cards_PrevCommitted), so they resolve on
 * the first or second try. The 82-caption graphic is a live one the NLE
 * builders never touch.
 */
const MAX_CAPTION_TRIES = 8;

/**
 * Find a usable block for a graphic without being told which caption.
 *
 * Used for the events an operator triggers from a BUTTON — goals, cards,
 * clock — which never pass through Maestro and so have no caption to read.
 * A graphic whose NLE caption is not among the first few tried simply keeps
 * the builder's data; the names tried are logged so the right one can be named
 * rather than searched for.
 */
export async function discoverBlock(layer, path) {
  const dir = stripFileName(path);
  if (!dir) return null;

  // Ask the definition FIRST rather than guessing a name. v_captions.refMap is
  // the authoritative list, and the names that matter are graphic-specific —
  // GoalScorer, Cards_PrevCommitted — not a scene-wide default. DefaultPopulated
  // is tried first only where it is actually offered.
  const definition = await getJson(path);
  const indexed = captionNames(definition);
  const names = [...new Set([
    ...(indexed.includes(DEFAULT_CAPTION) ? [DEFAULT_CAPTION] : []),
    ...indexed.filter((n) => n !== DEFAULT_CAPTION),
    // Nothing indexed: the definition may be unreadable, so still try the
    // conventional name before giving up.
    ...(indexed.length === 0 ? [DEFAULT_CAPTION] : []),
  ])].slice(0, MAX_CAPTION_TRIES);

  for (const name of names) {
    const found = await fetchBlock(layer, dir, name);
    if (found) return { block: found, via: `captions/${name}.json` };
  }

  const inline = definition ? findSceneDataBlock(definition, layer) : null;
  if (inline) return { block: inline.block, via: `inline at ${inline.path}` };

  return { block: null, tried: names };
}

/**
 * Read ONE named caption's block and put it in the shape the Broker expects.
 *
 * `dir` comes from TXLive's own presets, so it already carries this renderer's
 * scene_path and this graphic's version — neither knowable outside the app.
 * `preset` is the caption the operator actually used.
 */
export async function fetchBlock(layer, dir, preset) {
  const raw = await getJson(`${dir}/captions/${preset}.json`);
  if (!raw) return null;
  const data = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
  const found = findSceneDataBlock(data, layer);
  return found ? found.block : null;
}

/**
 * Every graphic this renderer can serve, as layer -> definition URL.
 *
 * WHY NOT JUST THE PRESETS. TXLive builds its presets from
 * `allocation_details.scene` alone, but the renderer is fetched with
 * `attachBothSceneCaption=true` — it carries more than one scene, and the NLE
 * post graphics (gfx_Post_PermScore_*) live in a different one from the live
 * graphics the operator drives. Reading only the first scene means those
 * layers have no URL, so their events keep the builder's baked data no matter
 * what else is right.
 *
 * Rather than guess which key the second scene hangs off, this walks the
 * renderer for anything scene-shaped: an object with a `path` and a `captions`
 * list. Each caption contributes `<base>/<path>/<name>/<version>/<name>.json`
 * — the same URL shape TXLive already uses.
 *
 * A layer served by two scenes keeps the FIRST one seen: allocation_details is
 * the renderer's own scene, and it is walked before anything nested.
 */
export function collectGraphicPaths(renderer, previewBase) {
  const base = String(previewBase || '').replace(/\/+$/, '');
  const paths = new Map();
  if (!base || !renderer || typeof renderer !== 'object') return paths;

  const addScene = (scenePath, captions) => {
    const clean = String(scenePath || '').replace(/^\/+|\/+$/g, '');
    if (!clean || !Array.isArray(captions)) return;
    for (const cap of captions) {
      const name = typeof cap === 'string' ? cap : cap?.name;
      if (!name || paths.has(name)) continue;
      const version = (typeof cap === 'object' && cap?.version) ? cap.version : 'latest';
      paths.set(name, `${base}/${clean}/${name}/${version}/${name}.json`);
    }
  };

  const seen = new Set();
  const walk = (node, depth) => {
    if (depth > 6 || !node || typeof node !== 'object' || seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) { node.forEach((v) => walk(v, depth + 1)); return; }

    // Scene-shaped: a path plus the captions that live under it.
    if (typeof node.path === 'string' && Array.isArray(node.captions)) {
      addScene(node.path, node.captions);
    }
    // The flattened form api.service copies onto the root.
    if (typeof node.scene_path === 'string' && Array.isArray(node.captions)) {
      addScene(node.scene_path, node.captions);
    }

    for (const v of Object.values(node)) walk(v, depth + 1);
  };

  walk(renderer, 0);
  return paths;
}

/**
 * Every graphic one scene-service scene record can serve, as layer ->
 * definition URL.
 *
 * The record is shaped differently from the renderer's: its path is
 * `scene_path` and each caption is `caption_name`. Disabled captions are left
 * out, as the renderer API leaves them out.
 */
export function sceneRecordPaths(scene, previewBase) {
  if (!scene || typeof scene !== 'object') return new Map();
  const captions = (Array.isArray(scene.captions) ? scene.captions : [])
    .filter((c) => c && !c.disabled)
    .map((c) => ({ name: c.caption_name ?? c.name, version: c.version }));
  return collectGraphicPaths({ path: scene.scene_path ?? scene.path, captions }, previewBase);
}
